import { useState } from 'react';
import { CalendarClock, History, PencilLine, Plus, TrendingUp, Undo2, Wallet } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { EmptyState } from '@/components/ui/EmptyState';
import { LoadingBlock } from '@/components/ui/Loader';
import { QueryError } from '@/components/ui/QueryError';
import { useApiQuery, type ApiQueryState } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { ApiRequestError } from '@/lib/apiClient';
import { formatCurrency, formatDate } from '@/lib/format';
import type { StaffSalary, StaffSalaryRecord } from '@/types/payroll';
import { payrollApi } from '../api/payroll.api';
import { SALARY_REVISION_LABEL } from '../constants';
import { describeIncrement, describeValue, lastIncrementOf } from '../format';
import { SalaryFormModal } from './SalaryFormModal';
import { SalaryPaymentsCard } from './SalaryPaymentsCard';
import { instituteToday } from '@/features/teacher/utils';
import type { SalaryPaymentMode } from '@/types/staff';

export interface StaffSalaryPanelProps {
  staffUuid: string;
  staffName: string;
  /** `YYYY-MM-DD` — no salary may start before it. */
  joinedOn: string | null;
  /** The profile's salary read, shared with the Overview card. */
  salary: ApiQueryState<StaffSalaryRecord | null>;
  canView: boolean;
  canManage: boolean;
  isOwner: boolean;
  /** How they are enrolled as being paid — what a new payment record starts on. */
  defaultPaymentMode?: SalaryPaymentMode | null;
}

/** Tile figures, in the profile's own card language. */
function Figure({ label, value, tone }: { label: string; value: string; tone?: 'crit' | 'ok' }) {
  return (
    <div className="bg-white dark:bg-slate-900 p-3.5 rounded-2xl border border-slate-200/90 dark:border-slate-800 text-center">
      <span className="text-xs font-bold text-slate-400 block uppercase">{label}</span>
      <span
        className={
          tone === 'crit'
            ? 'text-2xl font-black text-rose-600 dark:text-rose-400 mt-0.5 block tabular-nums'
            : tone === 'ok'
              ? 'text-2xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5 block tabular-nums'
              : 'text-2xl font-black text-slate-900 dark:text-white mt-0.5 block tabular-nums'
        }
      >
        {value}
      </span>
    </div>
  );
}

/** One salary's heads, earnings beside deductions. */
function Breakdown({ salary }: { salary: StaffSalary }) {
  const earnings = salary.lines.filter((line) => line.kind === 'EARNING');
  const deductions = salary.lines.filter((line) => line.kind === 'DEDUCTION');

  const row = (key: string, label: string, detail: string | null, amount: string) => (
    <div key={key} className="flex justify-between gap-3 text-slate-600 dark:text-slate-400">
      <span className="min-w-0 truncate">
        {label}
        {detail && <span className="ml-1 text-xs text-slate-400">({detail})</span>}
      </span>
      <span className="font-bold tabular-nums text-slate-900 dark:text-slate-100">{formatCurrency(amount)}</span>
    </div>
  );

  return (
    <div className="grid grid-cols-1 gap-4 text-sm sm:grid-cols-2">
      <div className="space-y-1.5">
        <div className="text-xs font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">Earnings</div>
        {row('basic', 'Basic Salary', null, salary.basic)}
        {earnings.map((line) =>
          row(
            line.componentUuid,
            line.name,
            line.calculation === 'PERCENT_OF_BASIC' ? describeValue(line.calculation, line.value) : null,
            line.amount,
          ),
        )}
        <div className="flex justify-between font-extrabold text-emerald-600 dark:text-emerald-400 pt-1 border-t border-slate-100 dark:border-slate-800">
          <span>Gross</span>
          <span className="tabular-nums">{formatCurrency(salary.grossEarnings)}</span>
        </div>
      </div>
      <div className="space-y-1.5 sm:border-l sm:border-slate-100 sm:pl-3 dark:sm:border-slate-800">
        <div className="text-xs font-bold text-rose-500 uppercase tracking-wider">Deductions</div>
        {deductions.length === 0 ? (
          <p className="text-slate-400">None</p>
        ) : (
          deductions.map((line) =>
            row(
              line.componentUuid,
              line.name,
              line.calculation === 'PERCENT_OF_BASIC' ? describeValue(line.calculation, line.value) : null,
              line.amount,
            ),
          )
        )}
        <div className="flex justify-between font-extrabold text-rose-600 dark:text-rose-400 pt-1 border-t border-slate-100 dark:border-slate-800">
          <span>Total deductions</span>
          <span className="tabular-nums">{formatCurrency(salary.totalDeductions)}</span>
        </div>
      </div>
    </div>
  );
}

/**
 * The profile's Payroll tab: the salary in force, one set to start later, and
 * every salary before them.
 *
 * Only the latest salary can be corrected or withdrawn — the server refuses
 * anything older, because later salaries were worked out from it — so those two
 * actions sit on the latest row alone.
 *
 * Payslips arrive with the monthly run, the next phase of payroll; until then
 * this tab is about the salary itself, and says so rather than showing a sample.
 */
export function StaffSalaryPanel({
  staffUuid,
  staffName,
  joinedOn,
  salary,
  canView,
  canManage,
  isOwner,
  defaultPaymentMode = null,
}: StaffSalaryPanelProps) {
  // The heads are needed only to fill in the form, so only a manager loads them.
  const heads = useApiQuery(
    () => (canManage ? payrollApi.listComponents() : Promise.resolve([])),
    [canManage],
  );
  const [formMode, setFormMode] = useState<'new' | 'correct' | null>(null);
  const [isConfirmingWithdraw, setIsConfirmingWithdraw] = useState(false);
  const [isWithdrawing, setIsWithdrawing] = useState(false);

  if (!canView) {
    return (
      <EmptyState
        icon={<Wallet className="h-5 w-5" />}
        title="Salaries are private"
        description="Only those who manage payroll can see what a member of staff is paid."
      />
    );
  }
  if (isOwner) {
    return (
      <EmptyState
        icon={<Wallet className="h-5 w-5" />}
        title="Not on payroll"
        description="The administrator account is not paid through payroll."
      />
    );
  }
  if (salary.isLoading) return <LoadingBlock label="Loading the salary" />;
  if (salary.error || !salary.data) {
    return (
      <QueryError
        title="Could not load the salary"
        error={salary.error ?? { message: 'The salary did not come back. Please try again.' }}
        onRetry={salary.refetch}
      />
    );
  }

  const record = salary.data;
  const { current, upcoming } = record;
  const latest = record.history[0] ?? null;
  const lastIncrement = lastIncrementOf(record.history, instituteToday());

  async function withdraw() {
    if (!latest) return;
    setIsWithdrawing(true);
    try {
      await payrollApi.withdrawSalary(latest.uuid);
      toast.success('Salary withdrawn', record.history.length > 1 ? 'The salary before it is back in force.' : undefined);
      setIsConfirmingWithdraw(false);
      salary.refetch();
    } catch (caught) {
      toast.error(
        'Could not withdraw the salary',
        caught instanceof ApiRequestError ? caught.message : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsWithdrawing(false);
    }
  }

  const actions = canManage && (
    <div className="flex flex-wrap gap-2">
      <Button
        size="sm"
        leftIcon={latest ? <TrendingUp className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
        isLoading={heads.isLoading}
        onClick={() => setFormMode('new')}
      >
        {latest ? 'Revise salary' : 'Set salary'}
      </Button>
      {latest && (
        <>
          <Button size="sm" variant="secondary" leftIcon={<PencilLine className="h-4 w-4" />} onClick={() => setFormMode('correct')}>
            Correct latest
          </Button>
          <Button size="sm" variant="ghost" leftIcon={<Undo2 className="h-4 w-4" />} onClick={() => setIsConfirmingWithdraw(true)}>
            Withdraw latest
          </Button>
        </>
      )}
    </div>
  );

  return (
    <div className="space-y-3.5 pt-1.5">
      {heads.error && canManage && (
        <QueryError title="Could not load the pay heads" error={heads.error} onRetry={heads.refetch} />
      )}

      {!latest ? (
        <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4">
          <EmptyState
            icon={<Wallet className="h-5 w-5" />}
            title="No salary set yet"
            description={
              canManage
                ? 'Set the joining salary — Basic pay plus the allowances and deductions that apply.'
                : 'Your school office has not set a salary for this member of staff yet.'
            }
            action={actions || undefined}
          />
        </div>
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-500 dark:text-slate-400">
              {current
                ? `In force since ${formatDate(current.effectiveFrom)} · ${SALARY_REVISION_LABEL[current.revisionType]}`
                : 'Nothing is in force yet.'}
            </p>
            {actions}
          </div>

          <p className="flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300">
            <TrendingUp className="h-4 w-4 text-emerald-600" aria-hidden />
            {lastIncrement ? (
              <span>
                Last increment:{' '}
                <strong className="text-emerald-600 dark:text-emerald-400">
                  {describeIncrement(lastIncrement.incrementPercent) ?? SALARY_REVISION_LABEL[lastIncrement.revisionType]}
                </strong>{' '}
                from {formatDate(lastIncrement.effectiveFrom)}
              </span>
            ) : (
              <span>No increment yet</span>
            )}
          </p>

          {current && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3.5">
              <Figure label="Basic" value={formatCurrency(current.basic)} />
              <Figure label="Monthly gross" value={formatCurrency(current.grossEarnings)} />
              <Figure label="Deductions" value={formatCurrency(current.totalDeductions)} tone="crit" />
              <Figure label="Net monthly pay" value={formatCurrency(current.netPay)} tone="ok" />
            </div>
          )}

          {upcoming && (
            <div className="flex items-start gap-3 rounded-2xl border border-sky-200 dark:border-sky-900/60 bg-sky-50 dark:bg-sky-950/30 p-3.5 text-sm">
              <CalendarClock className="mt-0.5 h-4 w-4 shrink-0 text-sky-600 dark:text-sky-400" aria-hidden />
              <p className="text-sky-900 dark:text-sky-200">
                <strong>{SALARY_REVISION_LABEL[upcoming.revisionType]}</strong> from{' '}
                {formatDate(upcoming.effectiveFrom)}: net pay {formatCurrency(upcoming.netPay)}
                {describeIncrement(upcoming.incrementPercent) && ` (${describeIncrement(upcoming.incrementPercent)} gross)`}.
              </p>
            </div>
          )}

          <SalaryPaymentsCard
            staffUuid={staffUuid}
            staffName={staffName}
            defaultMode={defaultPaymentMode}
            canManage={canManage}
          />

          {current && (
            <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 space-y-3">
              <h3 className="text-base font-extrabold text-slate-900 dark:text-white border-b border-slate-100 dark:border-slate-800 pb-2.5">
                Current breakdown
              </h3>
              <Breakdown salary={current} />
            </div>
          )}

          <div className="bg-white dark:bg-slate-900 border border-slate-200/90 dark:border-slate-800 rounded-2xl p-4 space-y-3">
            <h3 className="text-base font-extrabold text-slate-900 dark:text-white flex items-center gap-2 border-b border-slate-100 dark:border-slate-800 pb-2.5">
              <History className="h-4 w-4 text-indigo-600" aria-hidden />
              <span>Salary history</span>
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <caption className="sr-only">Every salary set for {staffName}, newest first</caption>
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-800 text-xs font-bold text-slate-500 dark:text-slate-400">
                    <th scope="col" className="py-2 px-3">Period</th>
                    <th scope="col" className="py-2 px-3">Reason</th>
                    <th scope="col" className="py-2 px-3 text-right">Basic</th>
                    <th scope="col" className="py-2 px-3 text-right">Gross</th>
                    <th scope="col" className="py-2 px-3 text-right">Net</th>
                    <th scope="col" className="py-2 px-3">Set by</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {record.history.map((row) => (
                    <tr key={row.uuid}>
                      <td className="py-2.5 px-3 whitespace-nowrap font-semibold text-slate-900 dark:text-white">
                        {formatDate(row.effectiveFrom)} – {row.effectiveTo ? formatDate(row.effectiveTo) : 'ongoing'}
                      </td>
                      <td className="py-2.5 px-3">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-slate-700 dark:text-slate-300">{SALARY_REVISION_LABEL[row.revisionType]}</span>
                          {describeIncrement(row.incrementPercent) && (
                            <Badge tone="success">{describeIncrement(row.incrementPercent)}</Badge>
                          )}
                        </div>
                        {row.note && <p className="text-xs text-slate-400">{row.note}</p>}
                      </td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{formatCurrency(row.basic)}</td>
                      <td className="py-2.5 px-3 text-right tabular-nums">{formatCurrency(row.grossEarnings)}</td>
                      <td className="py-2.5 px-3 text-right font-bold tabular-nums text-emerald-600 dark:text-emerald-400">
                        {formatCurrency(row.netPay)}
                      </td>
                      <td className="py-2.5 px-3 text-xs text-slate-500 dark:text-slate-400 whitespace-nowrap">
                        {row.setBy}
                        <br />
                        {formatDate(row.setAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      <p className="text-xs text-slate-400">
        Monthly payslips appear here once payroll is run for a month.
      </p>

      {formMode && (
        <SalaryFormModal
          isOpen
          mode={formMode}
          onClose={() => setFormMode(null)}
          staffUuid={staffUuid}
          staffName={staffName}
          joinedOn={joinedOn}
          record={record}
          components={heads.data ?? []}
          onSaved={() => {
            toast.success(formMode === 'correct' ? 'Salary corrected' : 'Salary saved');
            setFormMode(null);
            salary.refetch();
          }}
        />
      )}

      <ConfirmDialog
        isOpen={isConfirmingWithdraw}
        tone="danger"
        title="Withdraw the latest salary?"
        description={
          record.history.length > 1
            ? 'Use this for a salary set by mistake. The salary before it goes back into force, and the withdrawn one stays on record.'
            : 'Use this for a salary set by mistake. They will have no salary until one is set again; the withdrawn one stays on record.'
        }
        confirmLabel="Withdraw"
        isLoading={isWithdrawing}
        onConfirm={() => void withdraw()}
        onCancel={() => setIsConfirmingWithdraw(false)}
      />
    </div>
  );
}
